// apps/web/src/lib/theme.ts
// Light/dark theme switch. The choice lives in localStorage (device-level,
// like text size and haptics — not a server preference) and is applied as
// data-theme on <html>, which index.css token blocks key off. Dark is the
// default and the pre-theme behavior.
//
// forceTheme("light") pins the theme while the Get Started flow is mounted —
// the welcome mockup and question wizard are always the white version (owner)
// regardless of what the device has saved. Unmount restores the saved choice.
import { useSyncExternalStore } from "react";

export type Theme = "dark" | "light";

const KEY = "caloly.theme";
const listeners = new Set<() => void>();
let forced: Theme | null = null;

export function savedTheme(): Theme {
  try { return localStorage.getItem(KEY) === "light" ? "light" : "dark"; } catch { return "dark"; }
}

/** The theme actually on screen right now (forced wins over saved). */
export function currentTheme(): Theme {
  return forced ?? savedTheme();
}

function apply(): void {
  const t = currentTheme();
  document.documentElement.dataset.theme = t;
  // Browser chrome (PWA/Safari) follows the canvas.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", t === "light" ? "#f4f6f4" : "#000000");
  // iOS status bar text: dark glyphs on the light canvas, light on dark.
  // Dynamic import + catch — on web (no plugin) this is a silent no-op,
  // exactly the haptics.ts pattern.
  void import("@capacitor/status-bar")
    .then(({ StatusBar, Style }) => StatusBar.setStyle({ style: t === "light" ? Style.Light : Style.Dark }))
    .catch(() => { /* plugin unavailable (web) */ });
  listeners.forEach((fn) => fn());
}

/** Persist a user choice (Profile → Preferences → Appearance) and repaint. */
export function setTheme(t: Theme): void {
  try {
    if (t === "light") localStorage.setItem(KEY, "light");
    else localStorage.removeItem(KEY);
  } catch { /* storage unavailable */ }
  apply();
}

/** Pin the theme while a screen is mounted (null = back to saved). */
export function forceTheme(t: Theme | null): void {
  forced = t;
  apply();
}

/** Apply the saved theme before first paint (main.tsx). */
export function initTheme(): void {
  apply();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Reactive current theme — for JS-resolved colors (charts, SVG strokes). */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, currentTheme, () => "dark" as Theme);
}
